"use client"

import { useState } from "react"
import { Check, CheckCircle2, ChevronDown, ChevronUp, X } from "lucide-react"

import {
  CareerAxis,
  CareerTrack,
  ContinuousTrack,
} from "@/components/search/career-track"
import type { Verdict } from "@/components/search/graphite-search-results"
import { TINT, avatarHex, stageHex, stageTint, toneStyle } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import {
  fullRange,
  leadRole,
  type Axis,
  type Bar,
  type Role,
} from "@/lib/career-track"

/**
 * A result opened, from canvas 29a and 29b, with 30d and 30e as the phone.
 *
 * "The old sheet stacked six nested cards and repeated the result card's fields
 * underneath it." Here the career track from 27a is the top of the sheet, at
 * full width and with room for company, tenure and a year scale inside the bars
 * — the same shape the recruiter just scanned in the list, now legible. Under
 * it the roles read as a dated ledger keyed to that track, most recent first,
 * one line of substance each; the short facts and skills sit in a narrow right
 * column instead of below.
 *
 * The verdict actions and "result 1 of 24" stay in the header so the list can
 * be walked without closing.
 *
 * Presentational. Everything arrives as props.
 */

const LABEL =
  "text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground"

export type ResultProfileData = {
  candidateId: string
  name: string
  initials: string
  location?: string
  /** "Staff Engineer, Platform · Klarna" */
  currentRole?: string
  /** "5 years in role · 12y total" */
  tenureLine?: string
  /** "12 years · 4 roles", the line 30d puts over the continuous track. */
  careerLine?: string
  /** The same roles abutting across the candidate's own span, for the phone. */
  segments: Bar[]
  /** The scale that continuous track is drawn against — their own, not the page's. */
  ownAxis: Axis
  roles: Role[]
  bars: Bar[]
  /** Facts the contract actually carries. A row with no value is not drawn. */
  facts: { label: string; value: string }[]
  skills: string[]
  /** Skills the match engine leaned on, so they can be marked. */
  matchedSkills: Set<string>
  /** The model's reasoning, with the phrases it highlighted. */
  reasons: { text: string; highlights: string[] }[]
}

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/**
 * The reasoning, with the model's own phrases marked.
 *
 * Nodes, never an HTML string: this went through `dangerouslySetInnerHTML` on
 * the result card, so any angle bracket the model wrote was parsed as markup.
 */
function Highlighted({ text, highlights }: { text: string; highlights: string[] }) {
  const phrases = highlights.map((phrase) => phrase.trim()).filter(Boolean)
  if (phrases.length === 0) return <>{text}</>

  const parts = text.split(new RegExp(`(${phrases.map(escapeRegex).join("|")})`, "gi"))

  return (
    <>
      {parts.map((part, index) =>
        // `split` on a capturing group puts the matches at the odd indices.
        index % 2 === 1 ? (
          <mark
            key={index}
            className="rounded-[3px] bg-primary/15 px-1 py-px text-foreground"
          >
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  )
}

/** The model's reasoning. Drawn at every width — it is the answer to "why is
    this person in my results at all", which a phone needs as much as a desk. */
function WhyMatched({
  reasons,
  className,
}: {
  reasons: ResultProfileData["reasons"]
  className?: string
}) {
  if (reasons.length === 0) return null
  return (
    <div className={className}>
      <div className={cn(LABEL, "mb-2")}>Why this matched</div>
      <div className="flex flex-col gap-2">
        {reasons.map((reason, index) => (
          <p
            key={index}
            className="text-[12px] leading-[1.6] text-muted-foreground [text-wrap:pretty]"
          >
            <Highlighted text={reason.text} highlights={reason.highlights} />
          </p>
        ))}
      </div>
    </div>
  )
}

function VerdictButtons({
  verdict,
  isAdded,
  onVerdict,
  large,
  /** The linked-job lookup has not answered, so a Yes has nowhere to land yet. */
  isResolving,
}: {
  verdict: Verdict
  /** Already on the job this search feeds. */
  isAdded?: boolean
  onVerdict: (verdict: Verdict) => void
  large?: boolean
  isResolving?: boolean
}) {
  const shared = cn(
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border text-[11.5px] font-medium transition-colors",
    large ? "min-h-12 flex-1" : "px-[13px] py-[7px]",
  )

  /*
    The sheet has to say the same thing the row does. Leaving Yes live on a
    candidate the job already holds invites a second add of the same person.
  */
  const addedPill = (
    <span
      className={cn(shared, "border-transparent", large && "flex-1")}
      style={toneStyle(stageHex("hired"))}
    >
      <CheckCircle2 className="h-3 w-3" />
      Added to longlist
    </span>
  )

  /* Same order as the row: hidden first, carrying Added with it when both are
     true, so the one state with no way out of itself cannot happen here either. */
  if (verdict === "hidden") {
    return (
      <>
        {isAdded ? (
          addedPill
        ) : (
          <span
            className={cn(shared, "border-border text-muted-foreground", large && "flex-1")}
          >
            Hidden
          </span>
        )}
        <button
          type="button"
          onClick={() => onVerdict("none")}
          className={cn(shared, "border-primary text-brand-ink", large && "flex-1")}
        >
          Unhide
        </button>
      </>
    )
  }

  /* Added keeps its Hide, as the row does: hiding is about the result list, not
     the pipeline, and nothing here can take anyone off the pipeline. */
  if (isAdded) {
    return (
      <>
        {addedPill}
        <button
          type="button"
          onClick={() => onVerdict("hidden")}
          className={cn(
            shared,
            "border-border text-muted-foreground hover:text-foreground",
            large ? "" : "flex-none",
          )}
        >
          {large && <X className="h-3 w-3" />}
          Hide
        </button>
      </>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => onVerdict("yes")}
        aria-pressed={verdict === "yes"}
        disabled={isResolving}
        title={isResolving ? "Finding the job this search feeds…" : undefined}
        className={cn(
          shared,
          "border-primary text-brand-ink disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <Check className="h-3 w-3" />
        Yes
      </button>
      <button
        type="button"
        onClick={() => onVerdict("maybe")}
        aria-pressed={verdict === "maybe"}
        className={cn(
          shared,
          verdict === "maybe"
            ? "border-attention text-attention"
            : "border-border text-muted-foreground hover:text-foreground",
        )}
      >
        Maybe
      </button>
      {/*
        A word here, not the X the row uses: the sheet draws its own close in
        the same corner, and two X's side by side is one control asking to be
        misread as the other.
      */}
      <button
        type="button"
        onClick={() => onVerdict("hidden")}
        className={cn(
          shared,
          large ? "" : "flex-none",
          "border-border text-muted-foreground hover:text-foreground",
        )}
      >
        {large && <X className="h-3 w-3" />}
        Hide
      </button>
    </>
  )
}

export function ResultProfile({
  data,
  axis,
  verdict,
  isAdded,
  isResolving,
  onVerdict,
  position,
  total,
  onPrevious,
  onNext,
}: {
  data: ResultProfileData
  /** The page's shared axis, so the sheet and the row agree. */
  axis: Axis
  verdict: Verdict
  isAdded?: boolean
  /** The linked-job lookup is still running; a Yes cannot land yet. */
  isResolving?: boolean
  onVerdict: (verdict: Verdict) => void
  /** 1-based, for "result 3 of 24". */
  position?: number
  total?: number
  onPrevious?: () => void
  onNext?: () => void
}) {
  const hex = avatarHex(data.candidateId || data.name)

  /*
    The ledger and the track are the same four roles twice, so pointing at one
    lights the other. It reads both ways — a bar and its dated line are one
    thing, and which end you reach from is the reader's business — and it
    answers from the keyboard too, because a pairing only a mouse can see is
    not a pairing for everyone.
  */
  const [activeRoleId, setActiveRoleId] = useState<string | undefined>()

  /*
    30d describes one role at a time on the phone, so the narrow layout always
    has a role open — the one they are in until a segment is tapped.
  */
  const selectedRole =
    data.roles.find((role) => role.id === activeRoleId) ?? leadRole(data.roles)

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      {/* The sheet paints its own close in the top-right corner, so the header
          keeps a lane clear for it rather than running an action underneath. */}
      <header className="flex flex-none items-center gap-3.5 border-b bg-chrome py-3 pl-4 pr-11 @2xl:pl-[22px]">
        <span
          aria-hidden
          className="flex size-[38px] flex-none items-center justify-center rounded-full text-[13px] font-medium"
          style={{ color: hex, backgroundColor: stageTint(hex, TINT.pill) }}
        >
          {data.initials}
        </span>
        <span className="flex min-w-0 flex-col gap-[3px]">
          <span className="truncate text-[15px] font-semibold leading-[1.2]">
            {data.name}
          </span>
          {data.location && (
            <span className="truncate text-[11.5px] leading-none text-muted-foreground">
              {data.location}
            </span>
          )}
        </span>

        <span className="ml-auto flex flex-none items-center gap-4">
          {/* Walking the list without closing it — 29a keeps this in the header. */}
          {position !== undefined && total !== undefined && (
            <span className="flex items-center gap-2 font-mono text-[11px] text-muted-foreground">
              <button
                type="button"
                onClick={onPrevious}
                disabled={!onPrevious || position <= 1}
                aria-label="Previous result"
                className="flex size-6 items-center justify-center transition-colors hover:text-foreground disabled:opacity-40"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <span className="hidden @xl:inline">
                result {position} of {total}
              </span>
              <span className="@xl:hidden">
                {position}/{total}
              </span>
              <button
                type="button"
                onClick={onNext}
                disabled={!onNext || position >= total}
                aria-label="Next result"
                className="flex size-6 items-center justify-center transition-colors hover:text-foreground disabled:opacity-40"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
          <span className="hidden h-5 w-px bg-border @xl:block" />
          <span className="hidden items-center gap-2 @xl:flex">
            <VerdictButtons
              verdict={verdict}
              isAdded={isAdded}
              isResolving={isResolving}
              onVerdict={onVerdict}
            />
          </span>
        </span>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4 @2xl:p-[22px]">
        {/*
          The phone gets 30d: one unbroken track across their own years, cut
          into segments by tenure, with the tapped one described below. The
          shared axis is a comparison, and there is nothing here to compare
          against — the rest of the results are not on screen.
        */}
        {data.segments.length > 0 && (
          <section className="flex-none rounded-lg border bg-card p-4 @xl:hidden">
            <div className="mb-3 flex items-baseline gap-2.5">
              <span className={LABEL}>Career</span>
              {data.careerLine && (
                <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
                  {data.careerLine}
                </span>
              )}
            </div>
            <CareerAxis axis={data.ownAxis} className="mb-1.5 h-[11px]" />
            <ContinuousTrack
              segments={data.segments}
              activeId={selectedRole?.id}
              onSelect={setActiveRoleId}
            />

            {selectedRole && (
              <div className="mt-4 border-t pt-3.5">
                <div className="flex items-baseline gap-2.5">
                  <span className="min-w-0 text-[14.5px] font-semibold leading-[1.3]">
                    {selectedRole.title ?? selectedRole.company}
                  </span>
                  <span className="ml-auto flex-none font-mono text-[10.5px] text-muted-foreground">
                    {fullRange(selectedRole)}
                  </span>
                </div>
                {selectedRole.company && (
                  <div className="mt-1 text-[12px] leading-[1.4] text-muted-foreground">
                    {selectedRole.company}
                  </div>
                )}
                {selectedRole.description && (
                  <p className="mt-2.5 text-[12.5px] leading-[1.6] text-foreground/80 [text-wrap:pretty]">
                    {selectedRole.description}
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {data.reasons.length > 0 && (
          <section className="flex-none rounded-lg border bg-card p-4 @xl:hidden">
            <WhyMatched reasons={data.reasons} />
          </section>
        )}

        {/* The same track the row drew, with room to be read. */}
        <section className="hidden flex-none rounded-lg border bg-card p-4 @xl:block @2xl:px-[18px]">
          <div className="mb-3 flex items-baseline gap-3">
            {data.currentRole && (
              <span className="min-w-0 truncate text-[13px] leading-[1.3] text-foreground/80">
                {data.currentRole}
              </span>
            )}
            {data.tenureLine && (
              <span className="ml-auto flex-none font-mono text-[11px] text-muted-foreground">
                {data.tenureLine}
              </span>
            )}
          </div>

          {data.bars.length > 0 ? (
            <>
              <CareerAxis axis={axis} className="mb-1.5 h-[11px]" />
              <CareerTrack
                bars={data.bars}
                axis={axis}
                height={46}
                showTenure
                activeId={activeRoleId}
                onHoverRole={setActiveRoleId}
              />
            </>
          ) : (
            <p className="text-[12px] text-muted-foreground">
              No dated roles on record, so there is no track to draw.
            </p>
          )}
        </section>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 @3xl:grid-cols-[minmax(0,1fr)_292px]">
          <section className="hidden min-w-0 rounded-lg border bg-card p-4 @xl:block @2xl:px-[18px]">
            <div className={cn(LABEL, "mb-2")}>Roles</div>
            <div className="flex flex-col">
              {data.roles
                .slice()
                .reverse()
                .map((role) => (
                  <div
                    key={role.id}
                    onMouseEnter={() => setActiveRoleId(role.id)}
                    onMouseLeave={() => setActiveRoleId(undefined)}
                    onFocus={() => setActiveRoleId(role.id)}
                    onBlur={() => setActiveRoleId(undefined)}
                    /* Focusable so the pairing answers to a keyboard, but not a
                       control: there is nothing here to activate. */
                    tabIndex={0}
                    className={cn(
                      "grid grid-cols-[74px_minmax(0,1fr)] gap-3.5 border-t py-[11px] transition-colors outline-none",
                      role.isCurrent && "shadow-[inset_2px_0_0_var(--primary)]",
                      activeRoleId === role.id && "bg-primary/[0.06]",
                    )}
                  >
                    <div className="font-mono text-[10.5px] leading-[1.5] text-muted-foreground">
                      {fullRange(role)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-medium leading-[1.35]">
                        {role.title}
                        {role.company && (
                          <span className="font-normal text-muted-foreground">
                            {" "}
                            · {role.company}
                          </span>
                        )}
                      </div>
                      {role.description && (
                        <p className="mt-[3px] text-[11.5px] leading-[1.5] text-muted-foreground [text-wrap:pretty]">
                          {role.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              {data.roles.length === 0 && (
                <p className="border-t py-[11px] text-[12px] text-muted-foreground">
                  No roles on record.
                </p>
              )}
            </div>

            <WhyMatched reasons={data.reasons} className="mt-4 border-t pt-3.5" />
          </section>

          <section className="min-w-0 rounded-lg border bg-card p-4 @2xl:px-[18px]">
            <div className={cn(LABEL, "mb-2")}>Facts</div>
            <div className="flex flex-col">
              {data.facts.map((fact) => (
                <div
                  key={fact.label}
                  className="flex items-baseline gap-3 border-t py-[7px]"
                >
                  <span className="w-[74px] flex-none text-[11px] leading-[1.4] text-muted-foreground">
                    {fact.label}
                  </span>
                  <span className="min-w-0 flex-1 text-[12px] leading-[1.45] [text-wrap:pretty]">
                    {fact.value}
                  </span>
                </div>
              ))}
              {data.facts.length === 0 && (
                <p className="border-t py-[7px] text-[12px] text-muted-foreground">
                  Nothing on record.
                </p>
              )}
            </div>

            {data.skills.length > 0 && (
              <div className="mt-4 border-t pt-3.5">
                <div className={cn(LABEL, "mb-2")}>Skills</div>
                <div className="flex flex-wrap gap-1.5">
                  {/* The ones the match leaned on carry the brand edge. */}
                  {data.skills.slice(0, 7).map((skill) => (
                    <span
                      key={skill}
                      className={cn(
                        "inline-flex items-center rounded-md border px-2 py-[3px] text-[11px]",
                        data.matchedSkills.has(skill.toLowerCase())
                          ? "border-primary text-foreground"
                          : "border-border text-muted-foreground",
                      )}
                    >
                      {skill}
                    </span>
                  ))}
                  {data.skills.length > 7 && (
                    <span className="px-0.5 py-[3px] font-mono text-[10.5px] leading-[1.5] text-muted-foreground">
                      +{data.skills.length - 7}
                    </span>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* 30d pins the verdict where the thumb is; wide has it in the header. */}
      <div className="flex flex-none gap-2 border-t bg-chrome px-4 py-2.5 @xl:hidden">
        <VerdictButtons
          verdict={verdict}
          isAdded={isAdded}
          isResolving={isResolving}
          onVerdict={onVerdict}
          large
        />
      </div>
    </div>
  )
}
